// Figma: 4492:50484 — see stories/blocks/marketing-faq.stories.tsx
"use client";

import * as React from "react";

import { JsonLdScript } from "@/components/structured-data/json-ld-script";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionPrimitive,
} from "@/components/ui/accordion";
import { cn } from "@/lib/utils";

interface Question {
  question: string;
  answer: string;
}

interface MarketingFaqProps {
  title: string;
  questions: Question[];
  /** Which item starts open. Defaults to the first question. Pass `null` for all collapsed. */
  defaultOpen?: string | null;
  className?: string;
}

/**
 * Home FAQs (Figma node 4492:50484): two columns — a left heading and a right
 * list of grey-50 accordion cards, each with a 28px circular +/- toggle.
 */
export function MarketingFaq({
  title,
  questions,
  defaultOpen,
  className,
}: MarketingFaqProps) {
  const [openValue, setOpenValue] = React.useState<string | undefined>(
    defaultOpen === undefined
      ? questions[0]?.question
      : (defaultOpen ?? undefined)
  );

  const faqPageSchema =
    questions.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: questions.map((q) => ({
            "@type": "Question",
            name: q.question,
            acceptedAnswer: {
              "@type": "Answer",
              text: q.answer,
            },
          })),
        }
      : null;

  return (
    <div
      data-testid="marketing-faq"
      className={cn(
        "flex flex-col gap-10 lg:flex-row lg:gap-[112px]",
        className
      )}
    >
      {faqPageSchema && <JsonLdScript json={faqPageSchema} />}
      {/* Mobile title (Figma 4081:30392): 24px/-0.96px; desktop 32px. */}
      <h2 className="text-grey-950 text-[24px] leading-[1.2] tracking-[-0.96px] text-balance md:text-[32px] md:tracking-[-0.04em] lg:w-[260px] lg:shrink-0">
        {title}
      </h2>

      <Accordion
        value={openValue ? [openValue] : []}
        onValueChange={(value) => setOpenValue(value[0])}
        className="flex flex-1 flex-col gap-4 lg:max-w-[780px]"
      >
        {questions.map((q) => {
          const open = openValue === q.question;
          return (
            <AccordionItem
              key={q.question}
              value={q.question}
              className="border-grey-100 bg-grey-50 flex flex-col gap-4 rounded-2xl border p-6"
            >
              <AccordionPrimitive.Header className="flex">
                {/* Figma: the 4px header inset only exists on the OPEN card
                    (6508:29054); collapsed cards are exactly p-24. */}
                <AccordionPrimitive.Trigger
                  className={cn(
                    "text-grey-950 flex w-full min-w-0 items-center gap-4 text-left text-[16px] leading-[1.5] tracking-[-0.02em] outline-none",
                    open && "py-1"
                  )}
                >
                  <span className="min-w-0 flex-1 break-words">
                    {q.question}
                  </span>
                  <span
                    aria-hidden
                    className="border-grey-100 bg-grey-0 text-grey-950 flex size-7 shrink-0 items-center justify-center rounded-full border"
                  >
                    {open ? <MinusIcon /> : <PlusIcon />}
                  </span>
                </AccordionPrimitive.Trigger>
              </AccordionPrimitive.Header>
              <AccordionContent className="pb-0">
                <p className="text-grey-950 text-[16px] leading-[1.5] tracking-[-0.02em]">
                  {q.answer}
                </p>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>
    </div>
  );
}

// Exact Figma toggle icons (12×12, rounded strokes). fill=currentColor so the
// grey-950 text colour on the wrapping circle applies.
function PlusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M6 2.5C6.27614 2.5 6.5 2.72386 6.5 3V5.5H9C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H6.5V9C6.5 9.27614 6.27614 9.5 6 9.5C5.72386 9.5 5.5 9.27614 5.5 9V6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H5.5V3C5.5 2.72386 5.72386 2.5 6 2.5Z"
        fill="currentColor"
      />
    </svg>
  );
}

function MinusIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M9 5.5C9.27614 5.5 9.5 5.72386 9.5 6C9.5 6.27614 9.27614 6.5 9 6.5H3C2.72386 6.5 2.5 6.27614 2.5 6C2.5 5.72386 2.72386 5.5 3 5.5H9Z"
        fill="currentColor"
      />
    </svg>
  );
}
